import type { RefObject, FC } from 'react';
// eslint-disable-next-line import/no-duplicates
import React, { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { defineMessages, useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import EmojiAddIcon from '@atlaskit/icon/core/emoji-add';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { useContentState } from '@confluence/content-state/entry-points/useContentState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { useIsEligibleReactionsFeedbackPageReturn } from '@confluence/experiment-reactions-feedback-page-return/entry-points/useIsEligibleReactionsFeedbackPageReturn.ts';

import { ReactionsSummaryViewPlaceholder } from '../entry-points/ReactionsSummaryViewPlaceholder.tsx';
import { ReactionsSSRLoader } from '../entry-points/ReactionsSSRLoader.tsx';

import { ReactionsLoaderBackground as DelayedReactions } from './ReactionsLoader.tsx';
import type { ReactionLocation } from './reactionTypes.ts';
import { ReactionContainerType } from './reactionTypes.ts';
import { useReactionsQueryParams } from './hooks/useReactionsQueryParams.ts';
import { expValEquals } from '@confluence/feature-experiments';

const i18n = defineMessages({
	addNew: {
		id: 'reactions.view-page.byline.reactions.add.new',
		description: 'Message displayed in button to add a new reaction.',
		defaultMessage: 'Add new',
	},
	addReaction: {
		id: 'reactions.view-page.byline.reactions.add.reaction',
		description: 'Message displayed to add a reaction.',
		defaultMessage: 'Add reaction',
	},
});

const ViewPageReactionsContainerCommonStyles = xcss({
	display: 'flex',
	clear: 'both',
	overflow: 'visible',
	maxWidth: 'unset',
	margin: 'unset',
	marginBottom: 'space.025',
	marginTop: 'space.025',
	height: '28px',
});

const dotStyles = xcss({
	height: token('space.050'),
	width: token('space.050'),
	backgroundColor: 'color.background.brand.bold',
	borderRadius: 'radius.full',
	alignSelf: 'center',
	transition: 'opacity 300ms ease-in-out',
});

type ViewPageReactionsProps = {
	contentId: string;
	contentType?: string | null;
	spaceId?: string | null;
	contentSubType?: string | null;
	noFlexWrap?: boolean;
	noRelativeContainer?: boolean;
	bylineContainerRef?: RefObject<HTMLDivElement>;
	isCurrentUserContributor?: boolean;
};

const ReactionsComponent: FC<ViewPageReactionsProps> = ({
	contentId,
	contentType,
	spaceId,
	contentSubType,
	noFlexWrap,
	noRelativeContainer,
	bylineContainerRef = undefined,
	isCurrentUserContributor,
}) => {
	const { formatMessage } = useIntl();
	const [state] = useContentState();
	const { focusedPageReactionsId } = useReactionsQueryParams();
	const { edition } = useSessionData();
	const [reactionsRef, reactionsSectionInView] = useInView({
		rootMargin: '-100px 0px 0px 0px',
		triggerOnce: true,
	});

	const {
		isEligible: isEligibleReactionsFeedbackPageReturn,
		loading: isLoadingReactionsFeedbackPageReturn,
	} = useIsEligibleReactionsFeedbackPageReturn({
		contentId,
		isContributor: Boolean(isCurrentUserContributor),
		skip: !fg('cc-engagement-reactions-feedback-page-return-kill'),
	});

	!isLoadingReactionsFeedbackPageReturn &&
		isEligibleReactionsFeedbackPageReturn &&
		expValEquals('cc_engagement_reactions_feedback_page_return_aa', 'cohort', 'control');

	const showReactionsFeedbackPageReturnExp = useMemo(() => {
		return (
			!isLoadingReactionsFeedbackPageReturn &&
			isEligibleReactionsFeedbackPageReturn &&
			expValEquals('cc-engagement-reactions-feedback-page-return-exp', 'cohort', 'experiment')
		);
	}, [isLoadingReactionsFeedbackPageReturn, isEligibleReactionsFeedbackPageReturn]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (showReactionsFeedbackPageReturnExp) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'reactionsFeedbackPageReturnExp',
				},
			}).fire();
		}
	}, [showReactionsFeedbackPageReturnExp, createAnalyticsEvent]);

	// Used when we want to allow the user to select reactions from the summary view popup
	const customIcon = (
		<EmojiAddIcon
			testId="emoji-add-icon"
			color={token('color.icon.subtle')}
			label={formatMessage(i18n.addReaction)}
		/>
	);

	// Check whether we're on the server, or if in SPA but the page was initially SSR'd
	const isSSR =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__) &&
		fg('confluence_frontend_content_wrapper');

	// Check whether the reactions section is in view. This is the legacy condition
	const isInViewAndNotOnServer =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		!process.env.REACT_SSR && reactionsSectionInView && state?.isContentReady;

	const displayReactions = () => {
		if (contentType && spaceId) {
			// We want to load in the background ONLY if we have a query param, otherwise we can preload the query
			const ReactionsLoader = Boolean(focusedPageReactionsId)
				? DelayedReactions
				: ReactionsSSRLoader;

			return (
				<Flex
					testId="view-page-byline-reactions-wrapper"
					data-vc="view-page-byline-reactions-wrapper"
				>
					<ReactionsLoader
						contentId={contentId}
						location={contentType as ReactionLocation}
						containerId={spaceId}
						containerType={ReactionContainerType.SPACE}
						contentSubType={contentSubType}
						summaryViewEnabled
						summaryViewThreshold={1}
						allowSelectFromSummaryView
						useButtonAlignmentStyling
						summaryViewPlacement="bottom-start"
						animateNoReactionsText
						customAnalyticsAttributes={{
							edition,
						}}
						isSubtle
						reactionPickerTriggerIcon={customIcon}
						useDefaultStyledContainer
						noFlexWrap={noFlexWrap}
						noRelativeContainer={noRelativeContainer}
						reactionPickerTriggerText={formatMessage(i18n.addNew)}
						hoverableSummaryView
						optimisticallyLoadSummaryEmojis={isSSR}
						renderParticleEffectOnSummaryView
						bylineContainerRef={bylineContainerRef}
						renderParticleEffectOnLoad={showReactionsFeedbackPageReturnExp}
					/>
					{showReactionsFeedbackPageReturnExp && (
						<Box xcss={dotStyles} testId="reactions-feedback-experiment-dot" />
					)}
				</Flex>
			);
		} else {
			return <ReactionsSummaryViewPlaceholder />;
		}
	};

	return (
		<Box
			ref={reactionsRef}
			testId="view-page-byline-reactions"
			xcss={[ViewPageReactionsContainerCommonStyles]}
		>
			{isSSR || isInViewAndNotOnServer ? displayReactions() : <ReactionsSummaryViewPlaceholder />}
		</Box>
	);
};

export const ViewPageBylineReactions: FC<ViewPageReactionsProps> = (props) => {
	return (
		<TransparentErrorBoundary attribution={Attribution.NOTIFICATIONS}>
			<ReactionsComponent {...props} />
		</TransparentErrorBoundary>
	);
};
